import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Shield, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

const PERMISSION_LABELS = {
  can_manage_clients: 'Manage Clients',
  can_manage_talent: 'Manage Talent',
  can_create_shifts: 'Create Shifts',
  can_send_emails: 'Send Emails',
  can_view_billing: 'View Billing',
  can_edit_billing: 'Edit Billing',
  can_access_community: 'Community Feed',
};

const AdminPermissionsMatrix = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [permissionKeys, setPermissionKeys] = useState([]);
  const [savingUserId, setSavingUserId] = useState(null);
  const [dirty, setDirty] = useState({}); // {userId: {perm: bool}}

  const fetchData = async () => {
    try {
      const res = await api.get('/admin/admins/permissions');
      setAdmins(res.data.admins || []);
      setPermissionKeys(res.data.permission_keys || Object.keys(PERMISSION_LABELS));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const toggle = (userId, perm, currentValue) => {
    setDirty((d) => ({
      ...d,
      [userId]: { ...(d[userId] || {}), [perm]: !currentValue },
    }));
  };

  const currentValue = (admin, perm) => {
    const override = dirty[admin.user_id]?.[perm];
    return override !== undefined ? override : !!admin[perm];
  };

  const hasChanges = (userId) => Object.keys(dirty[userId] || {}).length > 0;

  const save = async (admin) => {
    const payload = dirty[admin.user_id] || {};
    if (Object.keys(payload).length === 0) return;
    setSavingUserId(admin.user_id);
    try {
      await api.patch(`/admin/admins/${admin.user_id}/permissions`, payload);
      toast.success(`Saved permissions for ${admin.email}`);
      setDirty((d) => { const n = { ...d }; delete n[admin.user_id]; return n; });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingUserId(null);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50/30 to-white">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-5">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/home')} data-testid="perms-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Home
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-cyan-500" /> Admin Permissions
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Toggle individual permissions per admin. Super Admins always have full access.
            </p>
          </div>
        </div>

        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm">Admins ({admins.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Admin</th>
                  {permissionKeys.map((p) => (
                    <th key={p} className="text-center px-2 py-2 font-medium text-gray-600 text-[11px]" title={PERMISSION_LABELS[p] || p}>
                      {PERMISSION_LABELS[p] || p}
                    </th>
                  ))}
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {admins.map((admin) => {
                  const isSuper = admin.admin_role === 'super_admin';
                  return (
                    <tr key={admin.user_id} className="border-b border-gray-100 hover:bg-gray-50/50" data-testid={`admin-row-${admin.user_id}`}>
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">{admin.email}</span>
                          {isSuper && <Badge className="mt-0.5 w-fit text-[10px] bg-cyan-100 text-cyan-700 border border-cyan-200">Super Admin</Badge>}
                          {!isSuper && <span className="text-[11px] text-gray-400">{admin.admin_role || 'admin'}</span>}
                        </div>
                      </td>
                      {permissionKeys.map((p) => {
                        const val = currentValue(admin, p);
                        return (
                          <td key={p} className="text-center px-2 py-3">
                            <input
                              type="checkbox"
                              checked={isSuper ? true : val}
                              disabled={isSuper}
                              onChange={() => toggle(admin.user_id, p, val)}
                              className="w-4 h-4 accent-cyan-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                              data-testid={`perm-${admin.user_id}-${p}`}
                            />
                          </td>
                        );
                      })}
                      <td className="px-3 py-3">
                        {!isSuper && hasChanges(admin.user_id) ? (
                          <Button
                            size="sm"
                            className="bg-emerald-500 hover:bg-emerald-600 text-white h-7 px-2 text-xs"
                            onClick={() => save(admin)}
                            disabled={savingUserId === admin.user_id}
                            data-testid={`save-perms-${admin.user_id}`}
                          >
                            {savingUserId === admin.user_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                            Save
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPermissionsMatrix;
